import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, TrendingUp } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";
import { useLang, fmtMoney } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const PitchRoomPanel = () => {
  const lang = useLang();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ title: "", pitch: "", amount: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/funding/pitches/mine`, headers()).then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(load, []);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/funding/pitches`, { ...form, amount: parseInt(form.amount, 10) || 0 }, headers());
      toast.success("Pitch submitted — FUNZ Angels will review it.");
      setForm({ title: "", pitch: "", amount: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (data === null) return null;
  const { pitches } = data;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="pitch-panel">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><TrendingUp className="h-5 w-5 text-[#F5B300]" /> Investor Pitch Room</p>
      <p className="mt-1 text-xs text-slate-500">Verified business owners pitch to FUNZ Angels — every pitch gets dual review + MFA approval.</p>
      {pitches.length > 0 && (
        <div className="mt-4 space-y-2.5">
          {pitches.map((p) => (
            <div key={p.id} data-testid={`pitch-${p.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold">{p.title}</p>
                <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                  p.status === "approved" ? "bg-[#22C55E]/10 text-[#22C55E]" : p.status === "rejected" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#F97316]/10 text-[#F97316]"
                }`}>{p.status.replace("_", " ")}</span>
              </div>
              <p className="mt-1 font-jbmono text-[10px] text-slate-500">{fmtMoney(p.amount, "INR", "₹")} · {String(p.created_at).slice(0, 10)}</p>
              {p.review_note && <p className="mt-1.5 text-xs text-slate-400">{p.review_note}</p>}
            </div>
          ))}
        </div>
      )}
      <div className="mt-5 space-y-3 border-t border-white/10 pt-5" data-testid="pitch-form">
        <p className="font-display text-sm font-bold">New pitch</p>
        <input data-testid="pitch-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Pitch title"
          className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
        <textarea data-testid="pitch-body" value={form.pitch} onChange={(e) => setForm({ ...form, pitch: e.target.value })} placeholder="Your business, traction, and how you'll use the funds…" rows={3}
          className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
        <div className="flex flex-wrap gap-2">
          <input data-testid="pitch-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount sought (₹)"
            className="flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
          <button data-testid="pitch-submit" onClick={submit} disabled={busy || !form.title.trim() || !form.pitch.trim() || !form.amount}
            className="rounded-full bg-gradient-to-r from-[#F5B300] to-[#F97316] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#09090B] disabled:opacity-50">
            {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : "Submit pitch"}
          </button>
        </div>
      </div>
    </section>
  );
};
